import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import AdminDashboard from 'layouts/AdminDashboard';
import Container from 'components/Container';
import {
  ActivityVolume,
  DemoAccountDTO,
  DemoCoachInsightSummaryDTO,
  DemoGenerationJobDTO,
  DemoIntegrityReportDTO,
  DemoPersonaDTO,
  DemoSimulationProfileDTO,
  DemoStatus,
  DemoUserSummaryDTO,
  ForecastResponse,
  InsightDTO,
  IntegrityCheckItemDTO,
  JobStatus,
  ScenarioType,
  SimulationStatus,
  deleteDemoAccount,
  deleteSimulationProfile,
  disableDemoAccount,
  duplicateDemoAccount,
  enableDemoAccount,
  getDemoAccount,
  getDemoCoachInsights,
  getDemoPersonas,
  getIntegrityCheck,
  listDemoUsers,
  listGenerationJobs,
  listSimulationProfiles,
  createSimulationProfile,
  resetDemoAccount,
  revertSimulation,
  runSimulation,
  tickDemoAccount,
  triggerGeneration,
  updateDemoAccount,
} from './DemoEngineService';

// ── Styling helpers ───────────────────────────────────────────────────────────

const STATUS_CHIP: Record<DemoStatus, { bg: string; color: string; label: string }> = {
  DRAFT:      { bg: '#9E9E9E22', color: '#616161', label: 'Draft' },
  GENERATING: { bg: '#1E88E522', color: '#1565C0', label: 'Generating' },
  ACTIVE:     { bg: '#2E7D3222', color: '#1B5E20', label: 'Active' },
  DISABLED:   { bg: '#C6282822', color: '#B71C1C', label: 'Disabled' },
  ERROR:      { bg: '#F9A82522', color: '#E65100', label: 'Error' },
};

const JOB_STATUS_CHIP: Record<JobStatus, { bg: string; color: string; label: string }> = {
  PENDING:   { bg: '#9E9E9E22', color: '#616161',  label: 'Pending'   },
  RUNNING:   { bg: '#1E88E522', color: '#1565C0',  label: 'Running'   },
  COMPLETED: { bg: '#2E7D3222', color: '#1B5E20',  label: 'Completed' },
  FAILED:    { bg: '#C6282822', color: '#B71C1C',  label: 'Failed'    },
};

const VOLUME_LABEL: Record<ActivityVolume, string> = {
  LOW: 'Low', MEDIUM: 'Medium', HIGH: 'High',
};

const fmtDate = (ts: string | null) =>
  ts ? new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const fmtDateTime = (ts: string | null) =>
  ts ? new Date(ts).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }) : '—';

const fmtDuration = (start: string | null, end: string | null): string => {
  if (!start || !end) return '—';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
};

// ── Sub-components ────────────────────────────────────────────────────────────

const SectionCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Card sx={{ mb: 3 }}>
    <CardContent>
      <Typography variant="h6" fontWeight={600} gutterBottom>{title}</Typography>
      <Divider sx={{ mb: 2 }} />
      {children}
    </CardContent>
  </Card>
);

const LV = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>{label}</Typography>
    <Typography variant="body2" component="div">{value ?? '—'}</Typography>
  </Box>
);

const StatusChip = ({ status }: { status: DemoStatus }) => {
  const cfg = STATUS_CHIP[status];
  return (
    <Chip label={cfg.label} size="small"
      sx={{ fontSize: '0.68rem', height: 22, bgcolor: cfg.bg, color: cfg.color, fontWeight: 600 }} />
  );
};

const JobStatusChip = ({ status }: { status: JobStatus }) => {
  const cfg = JOB_STATUS_CHIP[status];
  return (
    <Chip label={cfg.label} size="small"
      sx={{ fontSize: '0.68rem', height: 22, bgcolor: cfg.bg, color: cfg.color, fontWeight: 600 }} />
  );
};

// ── Tab panels ────────────────────────────────────────────────────────────────

interface OverviewTabProps {
  account: DemoAccountDTO;
  onAction: (action: string) => void;
  actionLoading: string | null;
}

const OverviewTab = ({ account, onAction, actionLoading }: OverviewTabProps) => (
  <Box>
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <SectionCard title="Account Details">
          <Grid container spacing={1}>
            <Grid item xs={6}><LV label="ID" value={`#${account.id}`} /></Grid>
            <Grid item xs={6}><LV label="Status" value={<StatusChip status={account.status} />} /></Grid>
            <Grid item xs={6}><LV label="Account Type" value={account.accountType} /></Grid>
            <Grid item xs={6}><LV label="Activity Volume" value={VOLUME_LABEL[account.activityVolume]} /></Grid>
            <Grid item xs={6}><LV label="Historical Months" value={`${account.historicalMonths}m`} /></Grid>
            <Grid item xs={6}><LV label="Forecast Months" value={`${account.futureMonths}m`} /></Grid>
            <Grid item xs={6}><LV label="Created" value={fmtDate(account.createdAt)} /></Grid>
            <Grid item xs={6}><LV label="Last Generated" value={fmtDate(account.lastGeneratedAt)} /></Grid>
            <Grid item xs={6}><LV label="Demo Users" value={account.userCount} /></Grid>
          </Grid>
        </SectionCard>
      </Grid>
      <Grid item xs={12} md={6}>
        <SectionCard title="Persona">
          {account.persona ? (
            <Grid container spacing={1}>
              <Grid item xs={12}><LV label="Name" value={account.persona.name} /></Grid>
              <Grid item xs={6}><LV label="Type" value={account.persona.accountType} /></Grid>
              <Grid item xs={6}><LV label="Location" value={account.persona.location} /></Grid>
              <Grid item xs={6}><LV label="Employment" value={account.persona.employmentType} /></Grid>
              <Grid item xs={6}><LV label="Income" value={account.persona.income != null
                ? `£${account.persona.income.toLocaleString()} / ${account.persona.incomeFrequency?.toLowerCase()}`
                : null} />
              </Grid>
              {account.persona.householdSize && (
                <Grid item xs={6}><LV label="Household Size" value={account.persona.householdSize} /></Grid>
              )}
              {account.persona.sector && (
                <Grid item xs={6}><LV label="Sector" value={account.persona.sector} /></Grid>
              )}
              <Grid item xs={12}>
                <LV label="Description" value={account.persona.description} />
              </Grid>
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">No persona assigned.</Typography>
          )}
        </SectionCard>
      </Grid>
      <Grid item xs={12}>
        <SectionCard title="Generation Flags">
          <Grid container spacing={1}>
            {[
              ['Recurring Transactions', account.generateRecurring],
              ['Ad-hoc Transactions',    account.generateAdHoc],
              ['Goals',                  account.generateGoals],
              ['Coach Insights',         account.generateCoachInsights],
              ['Behaviour Simulation',   account.enableBehaviourSimulation],
            ].map(([label, val]) => (
              <Grid item xs={6} sm={4} key={label as string}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={val ? 'On' : 'Off'}
                    size="small"
                    sx={{
                      fontSize: '0.65rem', height: 20, fontWeight: 600,
                      bgcolor: val ? '#2E7D3222' : '#9E9E9E22',
                      color: val ? '#1B5E20' : '#616161',
                    }}
                  />
                  <Typography variant="body2">{label as string}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </SectionCard>
      </Grid>
    </Grid>

    {/* Latest job */}
    {account.latestJob && (
      <SectionCard title="Latest Generation Job">
        <Grid container spacing={1}>
          <Grid item xs={6} sm={3}><LV label="Status" value={<JobStatusChip status={account.latestJob.status} />} /></Grid>
          <Grid item xs={6} sm={3}><LV label="Triggered By" value={account.latestJob.triggeredBy} /></Grid>
          <Grid item xs={6} sm={3}><LV label="Started" value={fmtDateTime(account.latestJob.startedAt)} /></Grid>
          <Grid item xs={6} sm={3}><LV label="Duration" value={fmtDuration(account.latestJob.startedAt, account.latestJob.completedAt)} /></Grid>
          <Grid item xs={6} sm={3}><LV label="Records Created" value={account.latestJob.recordsCreated.toLocaleString()} /></Grid>
          {account.latestJob.errorMessage && (
            <Grid item xs={12}>
              <Alert severity="error" sx={{ mt: 1 }}>{account.latestJob.errorMessage}</Alert>
            </Grid>
          )}
        </Grid>
      </SectionCard>
    )}

    {/* Quick actions */}
    <SectionCard title="Actions">
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        {(account.status === 'DRAFT' || account.status === 'ACTIVE' || account.status === 'ERROR') && (
          <Button
            variant="contained"
            onClick={() => onAction('generate')}
            disabled={actionLoading !== null}
            startIcon={actionLoading === 'generate' ? <CircularProgress size={14} /> : undefined}
          >
            Generate
          </Button>
        )}
        {account.status === 'ACTIVE' && (
          <Button variant="outlined" onClick={() => onAction('disable')} disabled={actionLoading !== null}>
            Disable
          </Button>
        )}
        {account.status === 'DISABLED' && (
          <Button variant="outlined" color="success" onClick={() => onAction('enable')} disabled={actionLoading !== null}>
            Enable
          </Button>
        )}
        {account.status === 'ACTIVE' && (
          <Button variant="outlined" onClick={() => onAction('reset')} disabled={actionLoading !== null}>
            Reset
          </Button>
        )}
        <Button variant="outlined" onClick={() => onAction('duplicate')} disabled={actionLoading !== null}>
          Duplicate
        </Button>
        <Button variant="outlined" color="error" onClick={() => onAction('delete')} disabled={actionLoading !== null}>
          Delete
        </Button>
      </Box>
    </SectionCard>
  </Box>
);

// ── Configuration tab ─────────────────────────────────────────────────────────

interface ConfigTabProps {
  account: DemoAccountDTO;
  personas: DemoPersonaDTO[];
  onSaved: () => void;
}

const ConfigTab = ({ account, personas, onSaved }: ConfigTabProps) => {
  const [name, setName] = useState(account.name);
  const [personaId, setPersonaId] = useState<number | ''>(account.persona?.id ?? '');
  const [volume, setVolume] = useState<ActivityVolume>(account.activityVolume);
  const [historicalMonths, setHistoricalMonths] = useState(account.historicalMonths);
  const [futureMonths, setFutureMonths] = useState(account.futureMonths);
  const [generateRecurring, setGenerateRecurring] = useState(account.generateRecurring);
  const [generateAdHoc, setGenerateAdHoc] = useState(account.generateAdHoc);
  const [generateGoals, setGenerateGoals] = useState(account.generateGoals);
  const [generateCoachInsights, setGenerateCoachInsights] = useState(account.generateCoachInsights);
  const [enableBehaviourSimulation, setEnableBehaviourSimulation] = useState(account.enableBehaviourSimulation);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      await updateDemoAccount(account.id, {
        name,
        personaId: personaId !== '' ? personaId : undefined,
        activityVolume: volume,
        historicalMonths,
        futureMonths,
        generateRecurring,
        generateAdHoc,
        generateGoals,
        generateCoachInsights,
        enableBehaviourSimulation,
      });
      setSaved(true);
      onSaved();
    } catch {
      setError('Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved(false)}>Changes saved.</Alert>}
      <SectionCard title="Basic Settings">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth label="Name" size="small" value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Persona</InputLabel>
              <Select
                value={personaId}
                label="Persona"
                onChange={(e) => setPersonaId(e.target.value as number | '')}
              >
                <MenuItem value="">None</MenuItem>
                {personas
                  .filter((p) => p.accountType === account.accountType)
                  .map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Activity Volume</InputLabel>
              <Select value={volume} label="Activity Volume"
                onChange={(e) => setVolume(e.target.value as ActivityVolume)}>
                <MenuItem value="LOW">Low</MenuItem>
                <MenuItem value="MEDIUM">Medium</MenuItem>
                <MenuItem value="HIGH">High</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth label="Historical Months" size="small" type="number"
              value={historicalMonths}
              onChange={(e) => setHistoricalMonths(Number(e.target.value))}
              inputProps={{ min: 1, max: 36 }}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth label="Forecast Months" size="small" type="number"
              value={futureMonths}
              onChange={(e) => setFutureMonths(Number(e.target.value))}
              inputProps={{ min: 0, max: 12 }}
            />
          </Grid>
        </Grid>
      </SectionCard>
      <SectionCard title="Generation Options">
        <Grid container spacing={1}>
          {[
            ['Recurring Transactions', generateRecurring, setGenerateRecurring] as const,
            ['Ad-hoc Transactions',    generateAdHoc,    setGenerateAdHoc]    as const,
            ['Goals',                  generateGoals,    setGenerateGoals]    as const,
            ['Coach Insights',         generateCoachInsights, setGenerateCoachInsights] as const,
            ['Behaviour Simulation',   enableBehaviourSimulation, setEnableBehaviourSimulation] as const,
          ].map(([label, val, setter]) => (
            <Grid item xs={12} sm={6} key={label}>
              <FormControlLabel
                control={<Switch checked={val} onChange={(e) => setter(e.target.checked)} />}
                label={label}
              />
            </Grid>
          ))}
        </Grid>
      </SectionCard>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
};

// ── Users tab ─────────────────────────────────────────────────────────────────

const UsersTab = ({ accountId }: { accountId: number }) => {
  const [users, setUsers] = useState<DemoUserSummaryDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    listDemoUsers(accountId)
      .then((res) => setUsers(res.data))
      .catch(() => setError('Could not load demo users.'))
      .finally(() => setLoading(false));
  }, [accountId]);

  if (loading) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  if (users.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No demo users generated yet. Run a generation to create users.
        </Typography>
      </Box>
    );
  }

  return (
    <Card>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Password</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Enabled</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{u.firstName} {u.lastName}</Typography>
                  <Typography variant="caption" color="text.secondary">#{u.id}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{u.email}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>DemoAccount2024!</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{fmtDate(u.createdAt)}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={u.enabled ? 'Active' : 'Disabled'}
                    size="small"
                    sx={{
                      fontSize: '0.65rem', height: 20, fontWeight: 600,
                      bgcolor: u.enabled ? '#2E7D3222' : '#9E9E9E22',
                      color: u.enabled ? '#1B5E20' : '#616161',
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

// ── Generation History tab ────────────────────────────────────────────────────

const JobHistoryTab = ({ accountId }: { accountId: number }) => {
  const [jobs, setJobs] = useState<DemoGenerationJobDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    listGenerationJobs(accountId)
      .then((res) => setJobs(res.data))
      .catch(() => setError('Could not load generation history.'))
      .finally(() => setLoading(false));
  }, [accountId]);

  if (loading) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  if (jobs.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography color="text.secondary">No generation jobs yet.</Typography>
      </Box>
    );
  }

  return (
    <Card>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Job ID</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Triggered By</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Started</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Duration</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Records</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map((job) => (
              <TableRow key={job.id} hover>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">#{job.id}</Typography>
                </TableCell>
                <TableCell>
                  <JobStatusChip status={job.status} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{job.triggeredBy ?? '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{fmtDateTime(job.startedAt)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{fmtDuration(job.startedAt, job.completedAt)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{job.recordsCreated.toLocaleString()}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {jobs.some((j) => j.errorMessage) && (
        <Box sx={{ p: 2 }}>
          {jobs.filter((j) => j.errorMessage).map((j) => (
            <Alert key={j.id} severity="error" sx={{ mb: 1 }}>
              Job #{j.id}: {j.errorMessage}
            </Alert>
          ))}
        </Box>
      )}
    </Card>
  );
};

// ── Stub tab ──────────────────────────────────────────────────────────────────

const StubTab = ({ title, description }: { title: string; description: string }) => (
  <Box sx={{ py: 6, textAlign: 'center' }}>
    <Typography variant="h6" fontWeight={600} gutterBottom>{title}</Typography>
    <Typography color="text.secondary" sx={{ maxWidth: 480, mx: 'auto' }}>{description}</Typography>
  </Box>
);

// ── Simulation tab ────────────────────────────────────────────────────────────

const SimulationTab = ({ accountId }: { accountId: number }) => {
  const [profiles, setProfiles] = useState<DemoSimulationProfileDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [actionError, setActionError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [newScenario, setNewScenario] = useState<ScenarioType>('INCREASED_DISCRETIONARY_SPENDING');
  const [creating, setCreating] = useState(false);
  const [runningId, setRunningId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DemoSimulationProfileDTO | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    listSimulationProfiles(accountId)
      .then((res) => setProfiles(res.data))
      .catch(() => setError('Could not load simulation profiles.'))
      .finally(() => setLoading(false));
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await createSimulationProfile(accountId, newScenario);
      setCreateOpen(false);
      load();
    } catch (e: any) {
      setActionError(e?.response?.data?.message ?? 'Could not create scenario.');
    } finally { setCreating(false); }
  };

  const handleRun = async (profileId: number) => {
    setRunningId(profileId);
    setActionError('');
    setActionMsg('');
    try {
      const res = await runSimulation(accountId, profileId);
      setActionMsg(`${res.data.scenarioLabel}: ${res.data.message}`);
      load();
    } catch (e: any) {
      setActionError(e?.response?.data?.message ?? 'Could not run simulation.');
    } finally { setRunningId(null); }
  };

  const handleRevert = async (profileId: number) => {
    setRunningId(profileId);
    setActionError('');
    try {
      const res = await revertSimulation(accountId, profileId);
      setActionMsg(`Reverted: ${res.data.message}`);
      load();
    } catch (e: any) {
      setActionError(e?.response?.data?.message ?? 'Could not revert simulation.');
    } finally { setRunningId(null); }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteSimulationProfile(accountId, deleteTarget.id);
      setDeleteTarget(null);
      load();
    } catch { setActionError('Could not delete scenario.'); }
    finally { setDeleting(false); }
  };

  if (loading) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>Simulation Scenarios</Typography>
          <Typography variant="body2" color="text.secondary">
            Apply real-world financial events to demo data to trigger insights and stress-test forecasts.
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => setCreateOpen(true)}>Add Scenario</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {actionError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>{actionError}</Alert>}
      {actionMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setActionMsg('')}>{actionMsg}</Alert>}

      {profiles.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No scenarios configured. Click "Add Scenario" to create one.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {profiles.map((p) => {
            const sc = SIM_STATUS_CHIP[p.status];
            const isRunning = runningId === p.id;
            return (
              <Card key={p.id} variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="body1" fontWeight={700}>{p.scenarioLabel}</Typography>
                        <Chip
                          label={p.status}
                          size="small"
                          sx={{ fontSize: '0.65rem', height: 20, fontWeight: 600, bgcolor: sc.bg, color: sc.color }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {p.scenarioDescription}
                      </Typography>
                      {p.lastRunAt && (
                        <Typography variant="caption" color="text.secondary">
                          Last run: {fmtDateTime(p.lastRunAt)}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleRun(p.id)}
                        disabled={isRunning}
                        startIcon={isRunning ? <CircularProgress size={12} color="inherit" /> : undefined}
                      >
                        Run
                      </Button>
                      {p.status === 'COMPLETED' && (
                        <Button size="small" variant="outlined" onClick={() => handleRevert(p.id)} disabled={isRunning}>
                          Revert
                        </Button>
                      )}
                      <Button size="small" color="error" onClick={() => setDeleteTarget(p)} disabled={isRunning}>
                        Delete
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Simulation Scenario</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Scenario type</InputLabel>
            <Select
              value={newScenario}
              label="Scenario type"
              onChange={(e) => setNewScenario(e.target.value as ScenarioType)}
            >
              {ALL_SCENARIO_TYPES.map((t) => (
                <MenuItem key={t} value={t}>{SCENARIO_LABELS[t]}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? 'Creating…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle>Delete scenario?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete <strong>{deleteTarget?.scenarioLabel}</strong>? Any data changes from previous runs will be reverted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ── Coach tab ─────────────────────────────────────────────────────────────────

const InsightCard = ({ insight }: { insight: InsightDTO }) => {
  const sc = SEVERITY_CHIP[insight.severity];
  return (
    <Card variant="outlined" sx={{ mb: 1.5, borderRadius: 2 }}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Chip
                label={insight.severity}
                size="small"
                sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700, bgcolor: sc.bg, color: sc.color }}
              />
              <Typography variant="caption" color="text.secondary">{insight.type}</Typography>
              {insight.completed && (
                <Chip label="Completed" size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#2E7D3222', color: '#1B5E20' }} />
              )}
            </Box>
            <Typography variant="body2" fontWeight={700} gutterBottom>{insight.title}</Typography>
            <Typography variant="body2" color="text.secondary">{insight.summary}</Typography>
            {insight.actionTitle && (
              <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="caption" fontWeight={600}>{insight.actionTitle}</Typography>
                {insight.estimatedMonthlySaving != null && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Est. saving: £{insight.estimatedMonthlySaving.toLocaleString()}/mo
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          {insight.amount != null && (
            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
              <Typography variant="h6" fontWeight={700} color="error.main">
                £{Math.abs(insight.amount).toLocaleString()}
              </Typography>
              {insight.bucket && (
                <Typography variant="caption" color="text.secondary">{insight.bucket}</Typography>
              )}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const ForecastTable = ({ forecast }: { forecast: ForecastResponse }) => {
  const fmtGBP = (v: number | null) =>
    v != null ? `£${Math.round(v).toLocaleString()}` : '—';
  const pct = (spend: number, budget: number) =>
    budget > 0 ? Math.round((spend / budget) * 100) : 0;

  const rows = [
    { label: 'Needs',   spend: forecast.projectedNeeds,   budget: forecast.needsBudget },
    { label: 'Wants',   spend: forecast.projectedWants,   budget: forecast.wantsBudget },
    { label: 'Savings', spend: forecast.projectedSavings, budget: forecast.savingsBudget },
  ];

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" gutterBottom display="block">
        Forecast · Month {forecast.month}/{forecast.year} · {forecast.daysRemaining} days remaining
        · Income {fmtGBP(forecast.monthlyIncome)}/mo
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Bucket</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">Projected</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">Budget</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">%</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(({ label, spend, budget }) => {
            const p = pct(spend, budget);
            return (
              <TableRow key={label}>
                <TableCell>{label}</TableCell>
                <TableCell align="right">{fmtGBP(spend)}</TableCell>
                <TableCell align="right">{fmtGBP(budget)}</TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: p > 100 ? 'error.main' : p > 85 ? 'warning.main' : 'success.main' }}
                  >
                    {p}%
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
          <TableRow>
            <TableCell colSpan={2} sx={{ fontWeight: 700 }}>Projected net</TableCell>
            <TableCell colSpan={2} align="right">
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{ color: forecast.projectedNet < 0 ? 'error.main' : 'success.main' }}
              >
                {fmtGBP(forecast.projectedNet)}
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};

const CoachTab = ({ accountId }: { accountId: number }) => {
  const [summaries, setSummaries] = useState<DemoCoachInsightSummaryDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    getDemoCoachInsights(accountId)
      .then((res) => setSummaries(res.data))
      .catch(() => setError('Could not load coach insights.'))
      .finally(() => setLoading(false));
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  if (summaries.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No demo users yet. Generate data first to see Coach insights.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={load}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>
      {summaries.map((s) => (
        <Box key={s.userId} sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>{s.fullName}</Typography>
            <Typography variant="body2" color="text.secondary">({s.email})</Typography>
          </Box>

          {s.insights.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No insights generated. The user may not have a financial profile set up yet.
            </Typography>
          ) : (
            s.insights.map((insight, i) => <InsightCard key={i} insight={insight} />)
          )}

          {s.forecast && (
            <Card variant="outlined" sx={{ borderRadius: 2, mt: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>Month-End Forecast</Typography>
                <Divider sx={{ mb: 1.5 }} />
                <ForecastTable forecast={s.forecast} />
              </CardContent>
            </Card>
          )}
        </Box>
      ))}
    </Box>
  );
};

// ── Integrity check tab ────────────────────────────────────────────────────────

const CHECK_STATUS_CFG: Record<IntegrityCheckItemDTO['status'], { color: string; bg: string; icon: string }> = {
  PASS: { color: '#1B5E20', bg: '#2E7D3222', icon: '✓' },
  FAIL: { color: '#B71C1C', bg: '#C6282822', icon: '✗' },
  WARN: { color: '#E65100', bg: '#F9A82522', icon: '⚠' },
  SKIP: { color: '#616161', bg: '#9E9E9E22', icon: '—' },
};

const IntegrityCheckTab = ({ accountId }: { accountId: number }) => {
  const [report, setReport] = useState<DemoIntegrityReportDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const runChecks = () => {
    setLoading(true);
    setError('');
    getIntegrityCheck(accountId)
      .then((res) => setReport(res.data))
      .catch(() => setError('Could not run integrity checks.'))
      .finally(() => setLoading(false));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>Data Integrity Checks</Typography>
          <Typography variant="body2" color="text.secondary">
            Verify that generated demo data is complete and consistent.
          </Typography>
        </Box>
        <Button variant="contained" onClick={runChecks} disabled={loading}>
          {loading ? 'Running…' : report ? 'Re-run Checks' : 'Run Checks'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Running checks…
          </Typography>
        </Box>
      )}

      {!loading && !report && (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Click "Run Checks" to validate the generated demo data.
          </Typography>
        </Box>
      )}

      {!loading && report && (
        <Box>
          {/* Summary bar */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h4" fontWeight={700}
                    sx={{ color: report.allPassed ? 'success.main' : 'error.main' }}>
                    {report.allPassed ? 'All Passed' : `${report.failed} Failed`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {report.userCount} user(s) · {report.checks.length} checks total
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {[
                    { label: 'Passed',   value: report.passed,   color: '#1B5E20' },
                    { label: 'Failed',   value: report.failed,   color: '#B71C1C' },
                    { label: 'Warnings', value: report.warnings, color: '#E65100' },
                    { label: 'Skipped',  value: report.skipped,  color: '#616161' },
                  ].map(({ label, value, color }) => (
                    <Box key={label} sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" fontWeight={700} sx={{ color }}>{value}</Typography>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Per-check results */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {report.checks.map((check) => {
              const cfg = CHECK_STATUS_CFG[check.status];
              return (
                <Card key={check.checkId} variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ py: '12px !important' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Chip
                        label={`${cfg.icon} ${check.status}`}
                        size="small"
                        sx={{
                          fontSize: '0.65rem', height: 22, fontWeight: 700,
                          bgcolor: cfg.bg, color: cfg.color,
                          flexShrink: 0, mt: 0.25,
                        }}
                      />
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{check.label}</Typography>
                        <Typography variant="caption" color="text.secondary">{check.detail}</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const TAB_LABELS = [
  'Overview',
  'Configuration',
  'Users',
  'Generation History',
  'Transactions',
  'Goals',
  'Coach',
  'Simulation',
  'Testing',
];

// ── Scenario metadata ─────────────────────────────────────────────────────────

const ALL_SCENARIO_TYPES: ScenarioType[] = [
  'NORMAL_MONTHLY_SPENDING',
  'INCREASED_DISCRETIONARY_SPENDING',
  'UNEXPECTED_LARGE_EXPENSE',
  'MISSED_DELAYED_INCOME',
  'ANNUAL_INSURANCE_RENEWAL',
  'NEW_RECURRING_SUBSCRIPTION',
  'INCREASED_SAVINGS_CONTRIBUTION',
  'GOAL_TARGET_DATE_CHANGE',
  'IRREGULAR_BUSINESS_REVENUE',
  'INCREASED_BUSINESS_OPERATING_COSTS',
];

const SCENARIO_LABELS: Record<ScenarioType, string> = {
  NORMAL_MONTHLY_SPENDING:           'Normal Monthly Spending',
  INCREASED_DISCRETIONARY_SPENDING:  'Increased Discretionary Spending',
  UNEXPECTED_LARGE_EXPENSE:          'Unexpected Large Expense',
  MISSED_DELAYED_INCOME:             'Missed / Delayed Income',
  ANNUAL_INSURANCE_RENEWAL:          'Annual Insurance Renewal',
  NEW_RECURRING_SUBSCRIPTION:        'New Recurring Subscription',
  INCREASED_SAVINGS_CONTRIBUTION:    'Increased Savings Contribution',
  GOAL_TARGET_DATE_CHANGE:           'Goal Target Date Change',
  IRREGULAR_BUSINESS_REVENUE:        'Irregular Business Revenue',
  INCREASED_BUSINESS_OPERATING_COSTS:'Increased Business Operating Costs',
};

const SIM_STATUS_CHIP: Record<SimulationStatus, { bg: string; color: string }> = {
  IDLE:      { bg: '#9E9E9E22', color: '#616161' },
  RUNNING:   { bg: '#1E88E522', color: '#1565C0' },
  PAUSED:    { bg: '#F9A82522', color: '#E65100' },
  COMPLETED: { bg: '#2E7D3222', color: '#1B5E20' },
};

const SEVERITY_CHIP: Record<'INFO' | 'WARNING' | 'ALERT', { bg: string; color: string }> = {
  INFO:    { bg: '#1E88E522', color: '#1565C0' },
  WARNING: { bg: '#F9A82522', color: '#E65100' },
  ALERT:   { bg: '#C6282822', color: '#B71C1C' },
};

const DemoAccountDetailPage = (): JSX.Element => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const accountId = Number(id);

  const [account, setAccount] = useState<DemoAccountDTO | null>(null);
  const [personas, setPersonas] = useState<DemoPersonaDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [tickMessage, setTickMessage] = useState('');

  const [tab, setTab] = useState(0);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    getDemoAccount(accountId)
      .then((res) => setAccount(res.data))
      .catch(() => setError('Could not load demo account.'))
      .finally(() => setLoading(false));
  }, [accountId]);

  useEffect(() => {
    load();
    getDemoPersonas().then((res) => setPersonas(res.data)).catch(() => {});
  }, [load]);

  // Auto-refresh every 5 s while the account is generating
  useEffect(() => {
    if (!account || account.status !== 'GENERATING') return;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [account, load]);

  const handleAction = async (action: string) => {
    if (action === 'delete') { setDeleteOpen(true); return; }
    setActionLoading(action);
    setActionError('');
    setTickMessage('');
    try {
      if (action === 'generate')   await triggerGeneration(accountId);
      if (action === 'enable')     await enableDemoAccount(accountId);
      if (action === 'disable')    await disableDemoAccount(accountId);
      if (action === 'reset')      await resetDemoAccount(accountId);
      if (action === 'duplicate') {
        const res = await duplicateDemoAccount(accountId);
        navigate(`/admin/demo-engine/accounts/${res.data.id}`);
        return;
      }
      if (action === 'tick') {
        const res = await tickDemoAccount(accountId);
        setTickMessage(res.data.message);
        return;
      }
      load();
    } catch (e: any) {
      setActionError(e?.response?.data?.message ?? `Could not perform action: ${action}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await deleteDemoAccount(accountId);
      navigate('/admin/demo-engine/accounts');
    } catch {
      setActionError('Could not delete demo account.');
      setDeleteOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <AdminDashboard>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </AdminDashboard>
    );
  }

  if (error || !account) {
    return (
      <AdminDashboard>
        <Container>
          <Alert severity="error" sx={{ mt: 4 }}>{error || 'Demo account not found.'}</Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            sx={{ mt: 2 }}
            onClick={() => navigate('/admin/demo-engine/accounts')}
          >
            Back to Demo Accounts
          </Button>
        </Container>
      </AdminDashboard>
    );
  }

  return (
    <AdminDashboard>
      <Box
        sx={{
          background: `linear-gradient(to bottom, ${alpha(theme.palette.primary.main, 0.06)}, transparent)`,
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Container>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <IconButton size="small" onClick={() => navigate('/admin/demo-engine/accounts')}>
                <ArrowBackIcon />
              </IconButton>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h4" fontWeight={700}>{account.name}</Typography>
                  <StatusChip status={account.status} />
                  {account.latestJob?.status === 'RUNNING' && (
                    <CircularProgress size={16} sx={{ ml: 0.5 }} />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  #{account.id} · {account.accountType} · {VOLUME_LABEL[account.activityVolume]} volume
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {account.status === 'ACTIVE' && (
                <Tooltip title="Add today's transactions (tick)">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleAction('tick')}
                      disabled={actionLoading === 'tick'}
                      sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                    >
                      {actionLoading === 'tick' ? 'Adding…' : '⚡ Tick'}
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Refresh">
                <IconButton onClick={load} size="small"><RefreshIcon /></IconButton>
              </Tooltip>
            </Box>
          </Box>

          {account.status === 'GENERATING' && (
            <Alert
              severity="info"
              icon={<CircularProgress size={16} color="inherit" />}
              sx={{ mb: 2 }}
            >
              Generating demo data… This page refreshes automatically every 5 seconds.
            </Alert>
          )}

          {actionError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>
              {actionError}
            </Alert>
          )}

          {tickMessage && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setTickMessage('')}>
              {tickMessage}
            </Alert>
          )}

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {TAB_LABELS.map((label) => (
                <Tab key={label} label={label} sx={{ textTransform: 'none', fontWeight: 500 }} />
              ))}
            </Tabs>
          </Box>

          {/* Tab content */}
          {tab === 0 && (
            <OverviewTab account={account} onAction={handleAction} actionLoading={actionLoading} />
          )}
          {tab === 1 && (
            <ConfigTab account={account} personas={personas} onSaved={load} />
          )}
          {tab === 2 && <UsersTab accountId={accountId} />}
          {tab === 3 && <JobHistoryTab accountId={accountId} />}
          {tab === 4 && (
            <StubTab
              title="Transactions"
              description="Browse all generated transactions for this demo account's users. Available in a future phase."
            />
          )}
          {tab === 5 && (
            <StubTab
              title="Goals"
              description="Inspect savings goals and milestones generated for this demo account. Available in a future phase."
            />
          )}
          {tab === 6 && <CoachTab accountId={accountId} />}
          {tab === 7 && <SimulationTab accountId={accountId} />}
          {tab === 8 && <IntegrityCheckTab accountId={accountId} />}
        </Container>
      </Box>

      {/* Delete confirmation */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs">
        <DialogTitle>Delete demo account?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will permanently delete <strong>{account.name}</strong> and all its generated data.
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminDashboard>
  );
};

export default DemoAccountDetailPage;
